import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  pharmacySaleService,
  PharmacySaleStatus,
  UpdatePharmacySaleDto,
  CreatePharmacySaleReturnDto,
} from '../pharmacySaleService';

export function usePharmacySales(
  page = 1,
  limit = 20,
  filters?: { search?: string; status?: PharmacySaleStatus; from?: string; to?: string },
) {
  return useQuery({
    queryKey: ['pharmacy-sales', page, limit, filters],
    queryFn: () => pharmacySaleService.getSales(page, limit, filters),
    placeholderData: (previous) => previous,
    staleTime: 30000,
  });
}

export function usePharmacySalesTrash(page = 1, limit = 20, search?: string) {
  return useQuery({
    queryKey: ['pharmacy-sales-trash', page, limit, search],
    queryFn: () => pharmacySaleService.getTrash(page, limit, { search }),
    placeholderData: (previous) => previous,
    staleTime: 30000,
  });
}

export function usePharmacySale(id?: string) {
  return useQuery({
    queryKey: ['pharmacy-sale', id],
    queryFn: () => pharmacySaleService.getSale(id!),
    enabled: !!id,
    staleTime: 30000,
  });
}

export function usePharmacySaleReturns(id?: string) {
  return useQuery({
    queryKey: ['pharmacy-sale-returns', id],
    queryFn: () => pharmacySaleService.getSaleReturns(id!),
    enabled: !!id,
    staleTime: 30000,
  });
}

/** Every mutation below can shift stock, so refresh the related pharmacy caches. */
function invalidateSaleCaches(queryClient: ReturnType<typeof useQueryClient>, id?: string) {
  queryClient.invalidateQueries({ queryKey: ['pharmacy-sales'] });
  queryClient.invalidateQueries({ queryKey: ['pharmacy-sales-trash'] });
  queryClient.invalidateQueries({ queryKey: ['pharmacy-stock'] });
  queryClient.invalidateQueries({ queryKey: ['pharmacy-drugs'] });
  queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  queryClient.invalidateQueries({ queryKey: ['top-sheet'] });
  if (id) queryClient.invalidateQueries({ queryKey: ['pharmacy-sale', id] });
}

export function useCreatePharmacySaleReturn() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreatePharmacySaleReturnDto }) =>
      pharmacySaleService.createSaleReturn(id, data),
    meta: { successMessage: 'Return recorded' },
    onSuccess: (_, variables) => {
      invalidateSaleCaches(queryClient, variables.id);
      queryClient.invalidateQueries({ queryKey: ['pharmacy-sale-returns', variables.id] });
    },
  });
}

export function useCreatePharmacySale() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: pharmacySaleService.createSale,
    meta: { successMessage: 'Sale recorded' },
    onSuccess: () => invalidateSaleCaches(queryClient),
  });
}

export function useUpdatePharmacySale() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePharmacySaleDto }) =>
      pharmacySaleService.updateSale(id, data),
    meta: { successMessage: 'Sale updated' },
    onSuccess: (_, variables) => invalidateSaleCaches(queryClient, variables.id),
  });
}

export function useTrashPharmacySale() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: pharmacySaleService.trashSale,
    meta: { successMessage: 'Sale moved to trash' },
    onSuccess: () => invalidateSaleCaches(queryClient),
  });
}

export function useRestorePharmacySale() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: pharmacySaleService.restoreSale,
    meta: { successMessage: 'Sale restored' },
    onSuccess: () => invalidateSaleCaches(queryClient),
  });
}

export function usePermanentlyDeletePharmacySale() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: pharmacySaleService.permanentDeleteSale,
    meta: { successMessage: 'Sale permanently deleted' },
    onSuccess: () => invalidateSaleCaches(queryClient),
  });
}

/** Bulk variants run silently so the UI can show a single summary toast. */
export function useBulkTrashPharmacySales() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: async (ids: string[]) => {
      for (const id of ids) {
        await pharmacySaleService.trashSale(id);
      }
    },
    onSuccess: () => invalidateSaleCaches(queryClient),
  });
}

export function useBulkRestorePharmacySales() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: async (ids: string[]) => {
      for (const id of ids) {
        await pharmacySaleService.restoreSale(id);
      }
    },
    onSuccess: () => invalidateSaleCaches(queryClient),
  });
}

export function useBulkPermanentlyDeletePharmacySales() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: async (ids: string[]) => {
      for (const id of ids) {
        await pharmacySaleService.permanentDeleteSale(id);
      }
    },
    onSuccess: () => invalidateSaleCaches(queryClient),
  });
}
